/**
 * Guardas de ruta.
 *
 *     <Route element={<RequireAuth />}>            ← todo lo de adentro pide sesión
 *       <Route element={<RequireRole minimum="admin" />}>
 *         <Route path="/admin/users" element={<UsersPage />} />
 *       </Route>
 *     </Route>
 *
 * `RequireAuth` recuerda a dónde iba la persona y la manda ahí después del
 * login: perder la ruta al vencer la sesión obliga a volver a navegar hasta
 * el caso que se estaba investigando.
 *
 * De nuevo: **esto no autoriza**. La API decide. Acá se evita mostrar una
 * pantalla que sólo puede terminar en 403.
 */
import type { ReactNode } from 'react';
import { Navigate, Outlet, useLocation } from 'react-router-dom';

import type { Role } from '@/api/types';
import { ForbiddenView } from '@/routes/ForbiddenView';
import { useAuth } from './useAuth';

export function RequireAuth(): ReactNode {
  const { isAuthenticated } = useAuth();
  const location = useLocation();

  if (!isAuthenticated) {
    return <Navigate to="/login" replace state={{ from: location.pathname + location.search }} />;
  }
  return <Outlet />;
}

/**
 * Rol mínimo para la rama. Si no alcanza, **no redirige**: muestra por qué.
 * Un redirect silencioso a la portada deja a la persona sin saber si el
 * enlace estaba mal o si le falta permiso, y termina en un ticket.
 */
export function RequireRole({ minimum }: { minimum: Role }): ReactNode {
  const { claims, can } = useAuth();

  if (!can(minimum)) {
    return <ForbiddenView required={minimum} actual={claims?.role ?? null} />;
  }
  return <Outlet />;
}
